import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useActiveClient } from "@/context/ActiveClientContext";
import { toast } from "sonner";
import {
  Chrome, Zap, CheckCircle2, XCircle, AlertTriangle, Loader2, Send, SkipForward,
  RefreshCw, ExternalLink, Copy, Circle, Download,
} from "lucide-react";

const SAMPLE_JOBS = [
  {
    label: "Plaid — Sr Backend Engineer (good fit for Jordan)",
    title: "Senior Backend Engineer", company: "Plaid", location: "Remote (US)", work_mode: "remote", salary: "$205k–$250k",
    posting_date: "2 days ago", job_url: "https://jobs.lever.co/plaid/senior-backend-engineer",
    job_text: "Senior Backend Engineer at Plaid. Fully remote within the US. You'll build payment and data infrastructure in Python and Go, working with PostgreSQL, Kafka and Kubernetes. 6+ years backend experience. We proudly sponsor H-1B and other work visas. Compensation: $205,000–$250,000 base plus equity.",
  },
  {
    label: "Ramp — Staff Engineer (no sponsorship, dealbreaker)",
    title: "Staff Software Engineer", company: "Ramp", location: "New York, NY (Hybrid)", work_mode: "hybrid", salary: "$230k",
    posting_date: "1 day ago", job_url: "https://boards.greenhouse.io/ramp/staff-swe",
    job_text: "Staff Software Engineer at Ramp. Hybrid in NYC. Strong Python/Go backend background required, payments experience a plus. Candidates must be legally authorized to work in the United States; we are unable to provide visa sponsorship for this role. Base salary $230,000.",
  },
  {
    label: "Datadog — Sr PMM (good fit for Priya)",
    title: "Senior Product Marketing Manager", company: "Datadog", location: "New York, NY", work_mode: "hybrid", salary: "$160k",
    posting_date: "5 days ago", job_url: "https://careers.datadoghq.com/senior-pmm",
    job_text: "Senior Product Marketing Manager at Datadog, hybrid in New York City. Own GTM, positioning and sales enablement for our B2B SaaS observability products. 4+ years PMM experience in B2B SaaS. Base salary around $160,000.",
  },
];

const statusIcon = { green: CheckCircle2, amber: AlertTriangle, red: XCircle };
const statusColor = { green: "text-emerald-400", amber: "text-amber-400", red: "text-rose-400" };

export default function Extension() {
  const { clients, activeClientId, setActiveClientId } = useActiveClient();
  const clientId = activeClientId;
  const [job, setJob] = useState(SAMPLE_JOBS[0]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoEval, setAutoEval] = useState(false);
  const [jdUrl, setJdUrl] = useState("");
  const [fetching, setFetching] = useState(false);

  const fetchFromUrl = async () => {
    if (!jdUrl.trim()) { toast.error("Paste a job posting URL"); return; }
    setFetching(true);
    try {
      const { data } = await api.post("/fetch-jd", { url: jdUrl.trim() });
      setJob({
        label: "Fetched from link", title: data.title || "", company: data.company || "",
        location: "", work_mode: "", salary: "", posting_date: "",
        job_url: data.job_url || jdUrl.trim(), job_text: data.job_text || "",
      });
      setResult(null);
      toast.success("JD fetched — review and click Analyse");
    } catch (e) { toast.error(apiError(e)); }
    finally { setFetching(false); }
  };

  const runEval = async (force = false) => {
    if (!clientId) { toast.error("Select a client first"); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/evaluate", { client_id: clientId, ...job, force });
      setResult(data);
      if (data.duplicate_of) toast.warning("Already logged for this client — possible duplicate");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  // when the client or job changes: auto-analyse if enabled, otherwise wait for the Analyse click
  useEffect(() => {
    if (!clientId) return;
    if (autoEval) runEval(false);
    else setResult(null);
    // eslint-disable-next-line
  }, [job, clientId]);

  const setStatus = async (status) => {
    if (!result) return;
    await api.put(`/evaluations/${result.id}`, { status });
    toast.success(`Marked as ${status}`);
    setResult({ ...result, status });
  };

  const copySummary = () => {
    if (!result) return;
    const text = `${result.decision} (${result.fit_score}/100) — ${job.title} @ ${job.company}\n${(result.reasons || []).map((r) => "• " + r).join("\n")}`;
    navigator.clipboard.writeText(text);
    toast.success("Summary copied");
  };

  const selected = clients.find((c) => c.id === clientId);

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-100 tracking-tight flex items-center gap-2">
          <Chrome className="w-7 h-7 text-indigo-400" /> Extension Simulator
        </h1>
        <p className="text-slate-400 text-sm mt-1">This is exactly how the on-page badge behaves. Pick a client, load a job, get a decision in seconds.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Mock browser / job page */}
        <div className="lg:col-span-3">
          <div className="rounded-xl overflow-hidden border border-slate-800/80 shadow-lg shadow-black/40">
            <div className="bg-[#0D0E15] px-4 py-2.5 flex items-center gap-2 border-b border-slate-800/60">
              <div className="flex gap-1.5"><span className="w-3 h-3 rounded-full bg-rose-500/70" /><span className="w-3 h-3 rounded-full bg-amber-500/70" /><span className="w-3 h-3 rounded-full bg-emerald-500/70" /></div>
              <div className="flex-1 mx-3 bg-[#12151F] border border-slate-800 rounded-md px-3 py-1 text-xs text-slate-500 font-mono truncate">{job.job_url}</div>
            </div>
            <div className="bg-[#12151F] p-5">
              <div className="mb-4">
                <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Paste a job posting link</label>
                <div className="flex gap-2 mt-1.5">
                  <input data-testid="jd-url-input" value={jdUrl} onChange={(e) => setJdUrl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") fetchFromUrl(); }}
                    placeholder="https://boards.greenhouse.io/... / careers page URL"
                    className="flex-1 bg-[#0D0E15] border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-indigo-500/60" />
                  <button data-testid="fetch-jd-btn" onClick={fetchFromUrl} disabled={fetching}
                    className="flex items-center gap-1.5 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold px-4 rounded-lg transition-colors disabled:opacity-60 whitespace-nowrap">
                    {fetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Fetch JD
                  </button>
                </div>
                <p className="text-[10px] text-slate-600 mt-1">Fetches the job text from the link. Login/JS-heavy boards (LinkedIn/Indeed) may block it — paste the text below if so.</p>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <div className="h-px flex-1 bg-slate-800" />
                <span className="text-[10px] text-slate-600 uppercase tracking-wider">or try a sample</span>
                <div className="h-px flex-1 bg-slate-800" />
              </div>

              <select data-testid="sample-job-select" className="w-full mb-4 bg-[#0D0E15] border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-indigo-500/60"
                value={job.label} onChange={(e) => { setJob(SAMPLE_JOBS.find((j) => j.label === e.target.value)); setResult(null); }}>
                {!SAMPLE_JOBS.some((j) => j.label === job.label) && <option value={job.label}>Fetched from link</option>}
                {SAMPLE_JOBS.map((j) => <option key={j.label} value={j.label}>{j.label}</option>)}
              </select>
              <h2 className="font-heading text-xl font-bold text-slate-100">{job.title}</h2>
              <div className="text-sm text-slate-400 mt-1">{job.company} · {job.location} · {job.posting_date}</div>
              <div className="flex gap-2 mt-3 flex-wrap text-xs">
                <span className="bg-slate-800/60 border border-slate-700/60 px-2 py-0.5 rounded text-slate-300">{job.work_mode}</span>
                <span className="bg-slate-800/60 border border-slate-700/60 px-2 py-0.5 rounded text-slate-300">{job.salary}</span>
              </div>
              <textarea data-testid="job-text" rows={7} value={job.job_text} onChange={(e) => setJob({ ...job, job_text: e.target.value })}
                className="mt-4 w-full bg-[#0D0E15] border border-slate-800 rounded-lg px-3 py-2.5 text-sm text-slate-300 leading-relaxed outline-none focus:border-indigo-500/60" />
              <p className="text-[11px] text-slate-600 mt-2">Editable — as if the extension extracted this from the page. In production this is auto-scraped from LinkedIn, Indeed, Greenhouse, Lever, Workday, etc.</p>
            </div>
          </div>
        </div>

        {/* Extension popup */}
        <div className="lg:col-span-2">
          <div className="sticky top-6 bg-[#141724] border border-slate-800 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden">
            <div className="bg-[#0D0E15] px-4 py-3 flex items-center gap-2 border-b border-slate-800/60">
              <div className="w-7 h-7 rounded-lg bg-indigo-500 flex items-center justify-center"><Zap className="w-4 h-4 text-white" /></div>
              <span className="font-heading font-bold text-slate-100 text-sm">ApplyPilot</span>
              <label className="ml-auto flex items-center gap-1.5 text-[11px] text-slate-400">
                <input type="checkbox" data-testid="auto-eval-toggle" checked={autoEval} onChange={(e) => setAutoEval(e.target.checked)} className="accent-indigo-500" /> Auto
              </label>
            </div>

            <div className="p-4 space-y-4">
              {/* Client selector */}
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Client</label>
                <select data-testid="ext-client-select" value={clientId} onChange={(e) => setActiveClientId(e.target.value)}
                  className="mt-1 w-full bg-[#0D0E15] border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-indigo-500/60">
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {selected && !selected.profile && <p className="text-[11px] text-amber-400 mt-1">This client has no profile yet — generate one first.</p>}
              </div>

              {loading && (
                <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                  <Loader2 className="w-7 h-7 animate-spin text-indigo-400" />
                  <span className="text-xs mt-2">Evaluating fit…</span>
                </div>
              )}

              {!loading && !result && (
                <div className="text-center py-8">
                  <div data-testid="jd-fetched" className="inline-flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1 mb-4">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Job description fetched
                  </div>
                  <p className="text-xs text-slate-500 mb-4 px-2">The extension read the JD from the page. Click below to get the Apply / Skip conclusion.</p>
                  <button data-testid="manual-eval" onClick={() => runEval(false)} className="bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors inline-flex items-center gap-2">
                    <Zap className="w-4 h-4" /> Analyse this job
                  </button>
                </div>
              )}

              {!loading && result && (
                <div className="space-y-4 animate-fade-up">
                  {result.duplicate_of && (
                    <div data-testid="dup-warning" className="flex items-start gap-2 text-xs bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded-lg px-3 py-2">
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Already logged for this client ({result.duplicate_of.status}). Possible duplicate.
                    </div>
                  )}

                  {/* Decision badge */}
                  <div className={`rounded-xl p-4 border ${result.decision === "APPLY" ? "bg-emerald-500/10 border-emerald-500/30" : "bg-rose-500/10 border-rose-500/30"}`}>
                    <div className="flex items-center justify-between">
                      <div data-testid="decision-badge" className={`font-heading font-extrabold text-lg tracking-tight ${result.decision === "APPLY" ? "text-emerald-400" : "text-rose-400"}`}>
                        {result.decision}
                        <div className="text-[11px] font-body font-medium text-slate-400 tracking-normal">
                          {result.decision === "APPLY" ? "Recommendation: apply now" : "Recommendation: skip this job"}
                        </div>
                      </div>
                      <div className="text-right">
                        <div data-testid="fit-score" className={`font-mono font-bold text-2xl ${result.decision === "APPLY" ? "text-emerald-400" : "text-rose-400"}`}>{result.fit_score}</div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider">Fit score</div>
                      </div>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${result.decision === "APPLY" ? "bg-emerald-400" : "bg-rose-400"}`} style={{ width: `${result.fit_score}%` }} />
                    </div>
                  </div>

                  {/* Dealbreakers */}
                  {result.dealbreakers?.length > 0 && (
                    <div className="text-xs bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2 text-rose-300">
                      <div className="font-semibold mb-0.5">Dealbreaker</div>
                      {result.dealbreakers.map((d, i) => <div key={i}>• {d}</div>)}
                    </div>
                  )}

                  {/* Criteria checklist */}
                  <div className="space-y-1.5">
                    {(result.criteria || []).map((c) => {
                      const Icon = statusIcon[c.status] || Circle;
                      return (
                        <div key={c.key} className="flex items-start gap-2 text-xs">
                          <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${statusColor[c.status] || "text-slate-500"}`} />
                          <div className="flex-1">
                            <span className="text-slate-300 font-medium">{c.label}</span>
                            <span className="text-slate-500"> — {c.explanation}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Reasons */}
                  <div className="bg-[#0D0E15] border border-slate-800 rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Why</div>
                    <ul className="space-y-1 text-xs text-slate-300">
                      {(result.reasons || []).map((r, i) => <li key={i}>• {r}</li>)}
                    </ul>
                  </div>

                  {/* Actions */}
                  <div className="grid grid-cols-2 gap-2">
                    <button data-testid="mark-applied" onClick={() => setStatus("applied")} className="flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold py-2 rounded-lg transition-colors">
                      <Send className="w-3.5 h-3.5" /> Mark applied
                    </button>
                    <button data-testid="mark-skipped" onClick={() => setStatus("skipped")} className="flex items-center justify-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold py-2 rounded-lg transition-colors">
                      <SkipForward className="w-3.5 h-3.5" /> Skip
                    </button>
                    <button data-testid="re-evaluate" onClick={() => runEval(true)} className="flex items-center justify-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold py-2 rounded-lg transition-colors">
                      <RefreshCw className="w-3.5 h-3.5" /> Re-evaluate
                    </button>
                    <button data-testid="copy-summary" onClick={copySummary} className="flex items-center justify-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold py-2 rounded-lg transition-colors">
                      <Copy className="w-3.5 h-3.5" /> Copy
                    </button>
                  </div>
                  <Link to="/tracker" className="flex items-center justify-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-300 pt-1">
                    <ExternalLink className="w-3.5 h-3.5" /> Open in dashboard
                  </Link>
                  {result.status && <div className="text-center text-[11px] text-slate-500">Current status: <span className="text-slate-300">{result.status}</span>{result.cached && " · cached"}</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
